export type AthenaRuntimeSubsystem =
	| "database"
	| "auth"
	| "billing"
	| "storage"
	| "event-ingress";

export interface AthenaRuntimeReadiness {
	fail(subsystem: AthenaRuntimeSubsystem, error: unknown): void;
	ready(subsystem: AthenaRuntimeSubsystem): void;
	waitFor(subsystem: AthenaRuntimeSubsystem): Promise<void>;
}

interface DeferredSlot {
	promise: Promise<void>;
	reject: (reason?: unknown) => void;
	resolve: () => void;
	settled: boolean;
}

function createSlot(): DeferredSlot {
	let resolve!: () => void;
	let reject!: (reason?: unknown) => void;
	const promise = new Promise<void>((res, rej) => {
		resolve = () => {
			res();
		};
		reject = (reason) => {
			rej(reason);
		};
	});
	promise.catch(() => undefined);
	return { promise, reject, resolve, settled: false };
}

export function createAthenaRuntimeReadiness(): AthenaRuntimeReadiness {
	const slots = new Map<AthenaRuntimeSubsystem, DeferredSlot>();
	const slot = (subsystem: AthenaRuntimeSubsystem): DeferredSlot => {
		const existing = slots.get(subsystem);
		if (existing) {
			return existing;
		}
		const created = createSlot();
		slots.set(subsystem, created);
		return created;
	};
	return {
		fail(subsystem, error) {
			const current = slot(subsystem);
			if (current.settled) {
				return;
			}
			current.settled = true;
			current.reject(error);
		},
		ready(subsystem) {
			const current = slot(subsystem);
			if (current.settled) {
				return;
			}
			current.settled = true;
			current.resolve();
		},
		waitFor(subsystem) {
			return slot(subsystem).promise;
		},
	};
}
